import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PolicyDeath } from '@models/policy-death';
import { Observable } from 'rxjs';
import { MMobileService } from '../../../core/services/common';
import {
  MailMessage,
  MailMessageRemoteConfig,
  MailParticularData,
  SendMessageRequest
} from '../../models/mail';
import {
  Document,
  Policy,
  PolicyExpedient,
  PolicyReceipt,
  PolicySinister
} from '../../models/policy';
import { PolicyHome } from '../../models/policy-home';
import { PendingReceipt } from '../../models/receipt';
import { AuthMirrorService } from '../core/auth/auth-mirror.service';
import { BaseService } from '../core/base.service';
import { OperationRegisterService } from '../core/operation-register.service';
import { BaseMailService } from './base-mail.service';

@Injectable({
  providedIn: 'root'
})
export class HomePoliciesService {
  constructor(
    private http: HttpClient,
    private base: BaseService,
    private baseMailService: BaseMailService,
    private mmobile: MMobileService,
    private authMirror: AuthMirrorService,
    private or: OperationRegisterService
  ) {}

  getHomePolicy({ policyNumber, type }: PolicyHome): Observable<PolicyHome | PolicyDeath> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}`;
    return this.http.get<PolicyHome>(
      url,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  getHomeReceipts(policy: PolicyHome): Observable<PolicyReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/receipts?companyId=${policy.companyId}`;
    const params = this.or.setHomeParams(policy);
    return this.http.get<PolicyReceipt[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getHomeReceipt(
    policy: PolicyHome,
    homeReceiptNumber: string
  ): Observable<PolicyReceipt> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/receipts/${homeReceiptNumber}?companyId=${policy.companyId}`;
    const params = this.or.setHomeParams(policy);
    return this.http.get<PolicyReceipt>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getHomePendingReceipts(
    homePolicyNumber: string,
    homeCompanyId: string,
    params: any
  ): Observable<PendingReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${homePolicyNumber}/receiptsPayment/receipts?companyId=${homeCompanyId}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<PendingReceipt[]>(url, { ...headers, params });
  }

  getHomeSinisters(policy: PolicyHome): Observable<PolicySinister[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/sinisters`;
    const params = this.or.setHomeParams(policy);
    return this.http.get<PolicySinister[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getHomeExpedients(
    policy: PolicyHome,
    homeSinisterCode: string
  ): Observable<PolicyExpedient[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/sinisters/${homeSinisterCode}/expedients`;
    const params = this.or.setHomeParams(policy);
    return this.http.get<PolicyExpedient[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getHomeReceiptDocument(
    homePolicyNumber: string,
    homeCompanyId: string,
    homeReceiptNumber: string,
    params: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${homePolicyNumber}/documents/receipts/${homeReceiptNumber}?companyId=${homeCompanyId}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getHomeParticularConditions(
    homePolicyNumber: string,
    params?: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${homePolicyNumber}/documents/particularConditions`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getHomeGeneralConditions(
    homePolicyNumber: string,
    params?: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${homePolicyNumber}/documents/generalConditions`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, {
      ...headers,
      params
    });
  }

  getHomeRenewalNotice(policy: Policy): Observable<Document> {
    const { policyNumber: homePolicyNumber } = policy || {};
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${homePolicyNumber}/documents/renewalNotice`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const params = this.or.calculateDocumentROParams(policy);
    return this.http.get<Document>(
      url,
      { ...headers, params }
    );
  }

  getCivilLiabilityCertificate(policy: Policy) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policy?.policyNumber}/documents/civilLiability`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const params = this.or.calculateDocumentROParams(policy);
    return this.http.get<Document>(url, { ...headers, params });
  }

  getAccidentCertificate(policy: Policy) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policy?.policyNumber}/documents/agreementAccidents`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const params = this.or.calculateDocumentROParams(policy);
    return this.http.get<Document>(url, { ...headers, params });
  }

  sendMessageMoreInfoSinister(
    name: string,
    policy: string,
    phone: string,
    email: string,
    nif: string,
    query: string
  ) {
    const forms: MailMessageRemoteConfig[] = this.mmobile.getCustomConfig().forms || [];
    const { user } = this.authMirror.authService.getState();
    const userIsCompany = this.authMirror.authService.checkUserIsCompany();
    const userEmail = userIsCompany ? user.legalRepresentative.email : user.email;
    const msg: MailMessage = {
      sendingFile: false,
      read: true,
      mailBoxId: '2',
      inboxId: '1',
      responseModeId: '1',
      type: '0',
      frmId: '288',
      groupId: '268',
      from: userEmail ? userEmail : null
    };

    const mailBoxForm = forms?.find(f => f.form === 'INFORMACIÓN_SINIESTROS_HOGAR');

    if (mailBoxForm) {
      msg.mailBoxId = mailBoxForm.mailBoxId;
      msg.inboxId = mailBoxForm.inboxId;
      msg.responseModeId = mailBoxForm.responseModeId;
      msg.type = mailBoxForm.type;
      msg.frmId = mailBoxForm.frmId;
      msg.groupId = mailBoxForm.groupId;
    }

    const data: MailParticularData[] = [];
    data.push({ field: 'nombre', value: name });
    data.push({ field: 'poliza', value: policy });
    data.push({ field: 'telefono', value: phone });
    data.push({ field: 'email', value: email });
    data.push({ field: 'nif', value: nif });
    data.push({ field: 'consulta', value: query });
    const request: SendMessageRequest = {
      mailboxIdentifier: user.documentNumber,
      particularData: data,
      message: msg
    };
    return this.baseMailService.sendMessage(request);
  }
}
